import React, { useEffect, useRef, useState } from "react";
import * as historyStore from "./historyStore";
import FilterSheet from "./FilterSheet";

/**
 * Toolbar "Recent" control: the last ~20 searches, newest activity first,
 * each replaying instantly from its snapshot when clicked.
 *
 * Desktop is an anchored popover; phones reuse the FilterSheet bottom sheet
 * (focus trap, body scroll lock, Escape handling come with it). The desktop
 * popover carries the same keyboard contract by hand: Escape closes and
 * returns focus to the trigger; entries are plain links, so Tab reaches
 * them in list order.
 *
 * Deliberately NOT built on search/DropDown — its outside-click check
 * compares event.target.id to the trigger id and fails on inner elements
 * (live bug, documented in FilterSheet too).
 */
export default function RecentPanel({ isPhone, onNavigate, onCleared }) {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);
    const btnRef = useRef(null);

    // Read at render: `open` toggling re-reads, and the parent re-renders on
    // every recorded search, so the list can't go stale while visible.
    const entries = historyStore.list();

    // Desktop outside-click + Escape. Attached only while open.
    useEffect(() => {
        if (!open || isPhone) return;
        const onDocClick = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        const onKey = (e) => {
            if (e.key === "Escape") {
                setOpen(false);
                if (btnRef.current) btnRef.current.focus();
            }
        };
        document.addEventListener("click", onDocClick, true);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("click", onDocClick, true);
            document.removeEventListener("keydown", onKey);
        };
    }, [open, isPhone]);

    if (!entries.length) return null;

    const relativeTime = (ts) => {
        const mins = Math.max(0, Math.round((Date.now() - ts) / 60000));
        if (mins < 1) return "just now";
        if (mins < 60) return mins + " min ago";
        const hours = Math.round(mins / 60);
        if (hours < 24) return hours + (hours === 1 ? " hour ago" : " hours ago");
        const days = Math.round(hours / 24);
        return days + (days === 1 ? " day ago" : " days ago");
    };

    const pick = (entry) => (e) => {
        e.preventDefault();
        setOpen(false);
        onNavigate(historyStore.entryHref(entry));
    };

    const clearAll = () => {
        historyStore.clear();
        setOpen(false);
        onCleared();
    };

    const listBody = (
        <div className="il-recent-list">
            {entries.map((entry) => (
                <a
                    key={entry.id}
                    className="il-recent-row"
                    href={historyStore.entryHref(entry)}
                    onClick={pick(entry)}
                >
                    {entry.q ? (
                        <span className="il-recent-q" aria-hidden="true">“ ”</span>
                    ) : (
                        <img src={entry.thumb || entry.src} alt="" loading="lazy" />
                    )}
                    <span className="il-recent-text">
                        <span className="il-recent-kind">
                            {entry.q
                                ? `“${entry.q.length > 30 ? entry.q.slice(0, 30) + '…' : entry.q}”`
                                : entry.src ? "Image search" : "Uploaded image"}
                        </span>
                        <span className="il-recent-meta">
                            {relativeTime(entry.updatedAt)} · {(entry.results || []).length} results
                        </span>
                    </span>
                </a>
            ))}
            <div className="il-recent-foot">
                <button type="button" className="btn btn-link btn-sm text-muted p-0" onClick={clearAll}>
                    Clear history
                </button>
            </div>
        </div>
    );

    return (
        <div className="il-recent-wrap position-relative" ref={wrapRef}>
            <button
                ref={btnRef}
                type="button"
                className="btn btn-sm btn-outline-secondary"
                aria-haspopup="true"
                aria-expanded={open}
                onClick={() => setOpen(!open)}
            >
                Recent
            </button>
            {open && (isPhone ? (
                <FilterSheet title="Recent searches" onClose={() => setOpen(false)}>
                    {listBody}
                </FilterSheet>
            ) : (
                <div className="il-recent-popover" role="menu" aria-label="Recent searches">
                    {listBody}
                </div>
            ))}
        </div>
    );
}
